import React, { useMemo, useState } from 'react';

import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Dropdown, DropdownItem } from 'reactstrap';
import styled from 'styled-components';
import { prop } from 'styled-tools';

import { StyledDropdownMenu, StyledDropdownToggle } from 'components/DropdownNavigation/dropdownNavigation.styles';

import ContentContainer from 'components/ContentContainer/ContentContainer';
import { ForexTablePriceCell } from 'components/ForexTable/components/ForexTablePriceCell';

import useCaxyLightstream from 'hooks/useCaxyLightstream';

import { epicToLabel } from 'utils/forexTableData';

const StyledContainer = styled(ContentContainer)`
  padding-left: 0;
  padding-right: 0;

  & > div:first-child {
    padding: 2rem 0 0;

    @media (min-width: ${(props) => props.theme.breakpoints.lg}) {
      display: flex;
    }
  }
`;

const StyledTable = styled.table`
  margin-left: 3rem;

  @media (max-width: ${(props) => props.theme.breakpoints.lg}) {
    width: 100%;
    margin-left: 0;
  }
`;

const StyledDropdown = styled(Dropdown)`
  width: 100%;

  @media (min-width: ${(props) => props.theme.breakpoints.lg}) {
    display: none;
  }

  @media (max-width: ${(props) => props.theme.breakpoints.md}) {
    padding: 0;
  }

  @media (max-width: ${(props) => props.theme.breakpoints.xl}) {
    padding: 0;
  }
`;

const Spacer = styled.div`
  padding-top: 0.5rem;
`;

const TitleTableContainer = styled.div<{ $scrollX: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: baseline;
  width: 100%;
  scrollbar-color: ${({ theme }) => theme.colors.ttRed} transparent;
  scrollbar-width: thin;

  @media (max-width: ${(props) => props.theme.breakpoints.xl}) {
    overflow-x: scroll;
  }
`;

const TabButton = styled.button<{ $isActive: boolean }>`
  padding: 1.1005rem 1.5rem;
  background-color: ${({ $isActive }) => ($isActive ? 'white' : 'transparent')};
  cursor: pointer;
  font-family: ${prop('theme.fonts.default')};
  text-decoration: none;
  color: black;
  font-size: 0.875rem;
  border: none;
  font-weight: ${({ $isActive }) => ($isActive ? '700' : '500')};
  text-align: left;

  &:hover {
    font-weight: 700;
  }
`;

const Tabnav = styled.div`
  background-color: ${(props) => props.theme.colors.secondaryGray5};
  padding: 0.5rem;
  min-width: 15.5rem;
  display: flex;
  flex-direction: column;

  @media (max-width: ${(props) => props.theme.breakpoints.lg}) {
    display: none;
  }
`;

const StyledTh = styled.th.attrs<{ $isLeftAligned?: boolean }>((props) => ({
  $isLeftAligned: props.$isLeftAligned || false,
}))`
  padding: 0.5rem 0;
  font-size: 0.75rem;
  text-align: ${({ $isLeftAligned }) => ($isLeftAligned ? 'left' : 'right')};
  font-weight: 600;
  text-transform: uppercase;
  white-space: nowrap;

  @media (max-width: ${(props) => props.theme.breakpoints.md}) {
    font-size: 0.66rem;
    padding-bottom: 0.75rem;
  }
`;

const StyledH2 = styled.h4`
  text-transform: uppercase;
  font-size: 1.5rem;
  font-family: ${({ theme }) => theme.fonts.fontFamilySecondary};
  margin-bottom: 1.05rem;
  margin-left: 3rem;

  @media (max-width: ${(props) => props.theme.breakpoints.lg}) {
    display: none;
  }
`;

const StyledTd = styled.td<{
  $hasLeftBorder?: boolean;
  $isLeftAligned?: boolean;
  $isNegative?: boolean;
  $monochrome?: boolean;
  $wideOK?: boolean;
}>`
  border-left: ${({ $hasLeftBorder, theme }) =>
    $hasLeftBorder ? `1px solid ${theme.colors.borderLightGray2}` : 'none'};
  padding: 0.75rem 0;
  text-align: ${({ $isLeftAligned }) => ($isLeftAligned ? 'left' : 'right')};
  color: ${({ $monochrome, $isNegative, theme }) =>
    $monochrome ? 'inherit' : $isNegative ? theme.colors.ttRed : theme.colors.upBlue};
  font-weight: 600;
  width: 100%;
  min-width: 9rem;
  font-size: 1rem;

  @media (max-width: ${(props) => props.theme.breakpoints.lg}) {
    font-size: 0.8125rem;
    min-width: 6rem;
  }

  @media (max-width: ${(props) => props.theme.breakpoints.xl}) {
    min-width: 7rem;
  }

  @media (max-width: ${(props) => props.theme.breakpoints.md}) {
    min-width: 5.8rem;
  }

  @media (min-width: ${(props) => props.theme.breakpoints.xl}) {
    min-width: ${({ $wideOK }) => ($wideOK ? '11.5rem' : '7rem')};
  }
`;

const StyledTableRow = styled.tr`
  border-bottom: 1px solid ${(props) => props.theme.colors.cloudGray};

  td:first-child {
    border-top-left-radius: 0.5rem;
    border-bottom-left-radius: 0.5rem;
  }

  &:first-child {
    border-top: 1px solid ${(props) => props.theme.colors.cloudGray};
  }
`;

const StyledDropdownItem = styled(DropdownItem)`
  font-weight: 500;
  padding: 0.625rem 1.25rem;
`;

export interface ForexTableTab {
  label: string;
  currencies: string[];
}

export interface ForexTableColumns {
  currency: string;
  BID: string | boolean;
  OFR: string | boolean;
  CPT: string | boolean;
  SPRD: string | boolean;
  SWAP_5_SHORT: string | boolean;
  SWAP_5_LONG: string | boolean;
}

export interface ForexTableProps {
  tabs: Array<ForexTableTab>;
  columns: ForexTableColumns;
}

const ForexTable = ({ tabs, columns }: ForexTableProps) => {
  const [previousTab, setPreviousTab] = useState<number>(0);
  const [activeTab, setActiveTab] = useState<number>(0);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const pushDataLayerEvent = (event: string, previousTabLabel: string, newTabLabel: string) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event,
      event_category: 'change tab',
      event_action: `To: ${newTabLabel}`,
      event_label: `From: ${previousTabLabel}`,
      interaction_type: `To: ${newTabLabel}`,
      interaction_value: `From: ${previousTabLabel}`,
    });
  };

  const handleTabClick = (tab: number) => {
    setPreviousTab(activeTab);
    setActiveTab(tab);
    pushDataLayerEvent('change_tab', tabs[previousTab].label, tabs[tab].label);
    pushDataLayerEvent('market_data_interaction', tabs[previousTab].label, tabs[tab].label);
  };

  const handleDropdownItemClick = (tab: number) => {
    setPreviousTab(activeTab);
    setActiveTab(tab);
    setDropdownOpen(true);
    pushDataLayerEvent('change_tab', tabs[previousTab].label, tabs[tab].label);
    pushDataLayerEvent('market_data_interaction', tabs[previousTab].label, tabs[tab].label);
  };

  const toggleDropdown = () => setDropdownOpen((prevState) => !prevState);

  const currencies = useMemo(() => {
    return tabs.reduce((acc: string[], tab): string[] => {
      return [...acc, ...tab.currencies];
    }, []);
  }, [tabs]);

  const wideOK = useMemo(() => columns.SWAP_5_SHORT === false && columns.SWAP_5_LONG === false, [columns]);

  const { data } = useCaxyLightstream(currencies);

  return (
    <StyledContainer>
      <StyledDropdown isOpen={dropdownOpen} toggle={toggleDropdown}>
        <StyledDropdownToggle>
          {tabs[activeTab].label}
          <FontAwesomeIcon size={'xs'} icon={faChevronDown} />
        </StyledDropdownToggle>
        <Spacer />
        <StyledDropdownMenu>
          {tabs.map((tab, index) => (
            <StyledDropdownItem
              key={index}
              data-testid={`tab-${index}`}
              $isActive={activeTab === index}
              onClick={() => handleDropdownItemClick(index)}
            >
              {tab.label}
            </StyledDropdownItem>
          ))}
        </StyledDropdownMenu>
      </StyledDropdown>
      <Tabnav role='tablist'>
        {tabs.map((tab, index) => (
          <TabButton key={index} $isActive={activeTab === index} onClick={() => handleTabClick(index)}>
            {tab.label}
          </TabButton>
        ))}
      </Tabnav>
      <TitleTableContainer $scrollX={!wideOK}>
        <StyledH2>{tabs[activeTab].label}</StyledH2>
        <StyledTable>
          <thead>
            <tr>
              <StyledTh $isLeftAligned={true}>{columns.currency}</StyledTh>
              {columns.BID !== false && <StyledTh>{columns.BID}</StyledTh>}
              {columns.OFR !== false && <StyledTh>{columns.OFR}</StyledTh>}
              {columns.CPT !== false && <StyledTh>{columns.CPT}</StyledTh>}
              {columns.SPRD !== false && <StyledTh>{columns.SPRD}</StyledTh>}
              {columns.SWAP_5_SHORT !== false && <StyledTh>{columns.SWAP_5_SHORT}</StyledTh>}
              {columns.SWAP_5_LONG !== false && <StyledTh>{columns.SWAP_5_LONG}</StyledTh>}
            </tr>
          </thead>
          <tbody>
            {tabs[activeTab].currencies.map((epic) => {
              // Spread is defined as the difference between offer & bid; since this data is not coming in from
              // lightstreamer, we calculate it to two significant figures here.
              // https://www.ig.com/en/glossary-trading-terms/spread-definition
              const spread = (
                Number(data.MARKET[epic]?.OFR.value ?? 0) - Number(data.MARKET[epic]?.BID.value ?? 0)
              ).toPrecision(2);

              return (
                <StyledTableRow key={epic}>
                  <StyledTd $isLeftAligned={true} $wideOK={wideOK} $monochrome>
                    {epicToLabel(epic)}
                  </StyledTd>
                  {columns.BID !== false && (
                    <StyledTd>
                      <ForexTablePriceCell market={data.MARKET[epic]} epicField='BID' />
                    </StyledTd>
                  )}
                  {columns.OFR !== false && (
                    <StyledTd $wideOK={wideOK}>
                      <ForexTablePriceCell market={data.MARKET[epic]} epicField='OFR' />
                    </StyledTd>
                  )}
                  {columns.CPT !== false && (
                    <StyledTd $isNegative={Number(data.MARKET[epic]?.CPT.value) < 0} $wideOK={wideOK}>
                      {data.MARKET[epic]?.CPT.value ?? '-'}
                    </StyledTd>
                  )}
                  {columns.SPRD !== false && (
                    <StyledTd $wideOK={wideOK} $monochrome>
                      {spread ?? '-'}
                    </StyledTd>
                  )}
                  {columns.SWAP_5_SHORT !== false && (
                    <StyledTd $wideOK={wideOK} $monochrome>
                      {data.MARKET[epic]?.SWAP_5_SHORT.value ?? '-'}
                    </StyledTd>
                  )}
                  {columns.SWAP_5_LONG !== false && (
                    <StyledTd $wideOK={wideOK} $monochrome>
                      {data.MARKET[epic]?.SWAP_5_LONG.value ?? '-'}
                    </StyledTd>
                  )}
                </StyledTableRow>
              );
            })}
          </tbody>
        </StyledTable>
      </TitleTableContainer>
    </StyledContainer>
  );
};

export default ForexTable;
